import { User } from "@supabase/supabase-js";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AccreditedToggle } from "../AccreditedToggle";
import { ProfilePictureUpload } from "../ProfilePictureUpload";
import { TitleSelect } from "@/components/profile/dropdowns/TitleSelect";
import { CountrySelect } from "@/components/profile/dropdowns/CountrySelect";
import { getCurrencySymbol } from "@/lib/currency";

interface PersonalInfoSectionProps {
  form: any;
  user: User;
}

export function PersonalInfoSection({ form, user }: PersonalInfoSectionProps) {
  const { data: currencies } = useQuery({
    queryKey: ["currencies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("currencies")
        .select("code,symbol,is_active")
        .eq("is_active", true)
        .order("code", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 1000 * 60 * 10,
  });

  const orderedCurrencies = useMemo(() => {
    const list = (currencies || []).slice().sort((a: any, b: any) => String(a.code).localeCompare(String(b.code)));
    const preferred = ["CAD", "USD"];
    const top = preferred
      .map((p) => list.find((c: any) => String(c.code).toUpperCase() === p))
      .filter(Boolean) as any[];
    const rest = list.filter((c: any) => !preferred.includes(String(c.code).toUpperCase()));
    return [...top, ...rest];
  }, [currencies]);

  // Always include the current code in options so the Select can display it immediately
  const currentCode = (String((() => {
    try { return form.getValues("defaultCurrency"); } catch { return ""; }
  })() || "")).toUpperCase();

  const displayCurrencies = useMemo(() => {
    const base = (orderedCurrencies && orderedCurrencies.length > 0)
      ? orderedCurrencies.map((c: any) => ({ code: String(c.code).toUpperCase(), symbol: c.symbol }))
      : ["CAD", "USD", "EUR", "GBP", "AUD"].map((code) => ({ code, symbol: getCurrencySymbol(code) }));

    const seen = new Set(base.map((c) => c.code));
    if (currentCode && !seen.has(currentCode)) {
      base.unshift({ code: currentCode, symbol: getCurrencySymbol(currentCode) });
    }
    return base;
  }, [orderedCurrencies, currentCode]);

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="border-b pb-2">
        <h4 className="text-lg font-medium">Personal Information</h4>
        <p className="text-sm text-muted-foreground">Your basic personal details</p>
      </div>

      {/* Row 1: Profile Picture + Accredited Toggle */}
      <div className="bg-card border rounded-lg p-4 md:p-6 overflow-hidden">
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-8">
          <div className="w-24 h-24 flex-shrink-0 mx-auto sm:mx-0">
            <ProfilePictureUpload user={user} />
          </div>
          <div className="flex-1 min-h-[96px] w-full min-w-0">
            <AccreditedToggle form={form} user={user} />
          </div>
        </div>
      </div>

      {/* Row 2: Title | First Name | Last Name */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-muted-foreground">Title</FormLabel>
              <FormControl>
                <TitleSelect value={field.value} onValueChange={field.onChange} placeholder="Select" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-muted-foreground">First Name *</FormLabel>
              <FormControl>
                <Input placeholder="First name" className="w-full" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-muted-foreground">Last Name *</FormLabel>
              <FormControl>
                <Input placeholder="Last name" className="w-full" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Row 3: Phone Number | Citizenship | Default Currency */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input placeholder="+1 (555) 123-4567" className="w-full" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="citizenship"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-muted-foreground">Citizenship</FormLabel>
              <FormControl>
                <CountrySelect value={field.value} onValueChange={field.onChange} placeholder="Select citizenship" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Default Currency */}
        <FormField
          control={form.control}
          name="defaultCurrency"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs text-muted-foreground">Default Currency</FormLabel>
              <FormControl>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="w-full h-9 text-sm">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {displayCurrencies.map((c: any) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol ? `${c.symbol} ${c.code}` : c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
